import { importLibrary } from '@googlemaps/js-api-loader';
import { useEffect, useRef, useState } from 'react';
import type { JobPinsItems, ScheduleRoutesDunny, ScheduleRoutesJobAddress } from './models/JobScheduleInterfaces'

interface GoogleMapsProps {
    pins: ScheduleRoutesJobAddress[];
    routes: ScheduleRoutesDunny[];
    refreshMap: boolean;
}

const redBackground = "#ff0000";
const greenBackground = "#33cc33";
const orangeBackground = "#ff8000";
//const blueBackground = "#0077b3";

const GoogleMapDunny = (props: GoogleMapsProps) => {
    const mapRef = useRef<HTMLDivElement>(null);
    const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
    const [errors, setErrors] = useState("");

    // Track active polylines so we can clear them when recalculating routes
    const activePolylinesRef = useRef<google.maps.Polyline[]>([]);
    const activeMarkersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
    const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);

    useEffect(() => {
        window.scrollTo(0, 0);
        getData();
    }, [props.refreshMap]);

    const getData = async () => {
        const { Map } = await importLibrary("maps");
        const office = new google.maps.LatLng(-37.817253, 145.299153);

        if (!mapRef.current) return;

        const map = new Map(mapRef.current, {
            center: office,
            zoom: 8,
            mapId: "allocationMapDunny"
        });
        setMapInstance(map);

        // Initialise a single shared native InfoWindow instance
        infoWindowRef.current = new google.maps.InfoWindow({
            disableAutoPan: false // Smoothly pans the map view to fit the open popup box
        });
    }

    useEffect(() => {
        if (!mapInstance) return;
        if (props.pins.length > 0) {
            addPins();
        }
        addRoutes();
    }, [mapInstance]);

    const addPins = async () => {
        if (!mapInstance) return;

        const { AdvancedMarkerElement, PinElement } = await importLibrary("marker");
        var bounds = new google.maps.LatLngBounds();

        for (var r = 0; r < props.pins.length; r++) {
            var position = { lat: props.pins[r].lat, lng: props.pins[r].lng };

            createMarker(mapInstance, props.pins[r], "", props.pins[r].colourId, props.pins[r].items);
            bounds.extend(position);
        }

        //center the map to the geometric center of all markers
        mapInstance.setCenter(bounds.getCenter());
        mapInstance.fitBounds(bounds);
        // set a minimum zoom
        // if you got only 1 marker or all markers are on the same address map will be zoomed too much.
        let mapZoom = mapInstance.getZoom() ?? 0;
        if (mapZoom > 15) {
            mapInstance.setZoom(15);
        }
    }

    const addRoutes = async () => {
        const { Route } = await importLibrary("routes");
        const { AdvancedMarkerElement, PinElement } = await importLibrary("marker");
        const bounds = new google.maps.LatLngBounds();

        //clear lines and markers
        activePolylinesRef.current.forEach(polyline => polyline.setMap(null));
        activePolylinesRef.current = [];
        activeMarkersRef.current.forEach(m => m.map = null);
        activeMarkersRef.current = [];
        if (infoWindowRef && infoWindowRef.current) {
            infoWindowRef.current.close();
        }

        
        //get a list of route colours to use to separate the routes
        var routeColours = ["blue", "purple", "pink", "yellow", "red", "green", "orange"];

        //1. Add Routes to Map
        for (var i = 0; i < props.routes.length; i++) {
            var officeName = "Bayswater North Office";
            if (props.routes[i].branchName) {
                officeName = props.routes[i].branchName + " Office";
            }
            var lat = props.routes[i].branchLatitude;
            var lng = props.routes[i].branchLongitude;
            if (lat === 0 && lng === 0) {
                //default to bayswater
                lat = -37.817253;
                lng = 145.299153;
            }
            const office = new google.maps.LatLng(lat, lng);
            var latlnglist: google.maps.LatLng[] = [];
            latlnglist.push(office);

            //get way points
            var waypts: google.maps.routes.Waypoint[] = [];
            for (var a = 0; a < props.routes[i].jobAddresses.length; a++) {
                var waypt = new google.maps.LatLng
                    ({
                        lat: props.routes[i].jobAddresses[a].lat,
                        lng: props.routes[i].jobAddresses[a].lng
                    });
                latlnglist.push(waypt);
                waypts.push({ location: waypt, vehicleStopover: true });
                //add waypt marker popup
                if (mapInstance) {
                    var jobAddr = props.routes[i].jobAddresses[a];

                    var wayPtMarker = createMarker(mapInstance, jobAddr, props.routes[i].orderManual, jobAddr.colourId, jobAddr.items)
                    activeMarkersRef.current.push(wayPtMarker);
                }
            }
            latlnglist.push(office);

            //add the google route with way points
            var googleRoute: google.maps.routes.ComputeRoutesRequest = {
                origin: office,
                destination: office,
                intermediates: waypts,
                travelMode: 'DRIVING',
                fields: ["path", "legs"]
            };

            const { routes } = await Route.computeRoutes(googleRoute);

            if (routes) {
                const mainRoute = routes[0];

                // Build native vectors directly onto the DOM instance canvas
                const newLines = mainRoute.createPolylines({
                    polylineOptions: {
                        map: mapInstance,
                        strokeColor: routeColours[i % routeColours.length],
                        strokeWeight: 5,
                        strokeOpacity: 0.9,
                    },
                });

                //Add lines
                newLines.forEach((polyline) => {
                    // Overlay the polyline onto our active map object
                    polyline.setMap(mapInstance);
                    activePolylinesRef.current.push(polyline);

                    // Extend map viewport boundaries to contain the complete path
                    polyline.getPath().forEach((latLng) => bounds.extend(latLng));
                });

                const mainRoutePath = mainRoute.path;
                if (mainRoutePath && mainRoutePath.length > 0) {
                    const startCoords = mainRoutePath[0];

                    //add a start / end marker as both are the office
                    const startEndElement = document.createElement('div');
                    startEndElement.innerHTML = `
                        <div style="background-color: #34A853; color: white; padding: 6px 10px; border-radius: 20px; font-weight: bold; font-family: sans-serif; box-shadow: 0 2px 6px rgba(0,0,0,0.3); border: 2px solid white;">
                          ` + officeName + `
                        </div>
                    `;

                    // Instantiate Advanced Markers
                    const startMarker = new AdvancedMarkerElement({
                        map: mapInstance,
                        position: startCoords,
                        content: startEndElement,
                        title: officeName
                    });

                    activeMarkersRef.current.push(startMarker);
                }
            }

            // Refit the camera view cleanly around all constructed route layouts
            if (mapInstance) {
                mapInstance.fitBounds(bounds);
            }
        }

    }

    const createMarker = (mapInstance: google.maps.Map, wayPoint: ScheduleRoutesJobAddress, truckName: string, colourId: number, items: JobPinsItems[]) => {
        var position = { lat: wayPoint.lat, lng: wayPoint.lng };
        var address = wayPoint.address;

        var pinBackground = new google.maps.marker.PinElement({
            background: colourId === 1 ? greenBackground : colourId === 2 ? orangeBackground : redBackground,
        });

        var markerText = getMarkerText(address, truckName);
        for (var i = 0; i < items.length; i++) {
            var item = items[i];
            markerText += getMarkerItemText(item.item, item.quantityType, " - " + item.scheduleType, " - " + item.scheduleDateFormatted);
        }

        var infopopup = new google.maps.InfoWindow({ content: markerText });
        const marker = new google.maps.marker.AdvancedMarkerElement({
            position: position,
            map: mapInstance,
            title: truckName,
            content: pinBackground
        });

        google.maps.event.addListener(marker, 'click', function () {
            infopopup.open(mapInstance, marker);
        });

        google.maps.event.addListener(mapInstance, 'click', (function (infopopup) {
            return function () {
                infopopup.close();
            }
        })(infopopup));

        return marker;
    }

    const getMarkerText = (address: string, name: string) => {
        return '<div>' +
            '<h3>' + address + '</h3>' +
            '<span>' + name + '</span>' +
            '</div>';
    }

    const getMarkerItemText = (item: string, quantityType: string, scheduleType: string, scheduleDateFormatted: string) => {
        var quantityText = quantityType === "" ? "" : " x " + quantityType;

        return '<div>' +
            '<span>' + item + quantityText + scheduleType + scheduleDateFormatted + '</span>' +
            '</div>';
    }

    return (
        <div>
            <div ref={mapRef} className="map" id="map"></div>
            {/*<div className="hidden" id="directions-panel"></div>*/}
            <div className="makeitred" id="error-panel"></div>
        </div>
    );
}
export default GoogleMapDunny;



//                const errorPanel = document.getElementById("error-panel") as HTMLElement;
//                const summaryPanel = document.getElementById("directions-panel") as HTMLElement;
//                if (summaryPanel) {
//                    summaryPanel.innerHTML += "";

//                    if (errorPanel) {
//                        //add a note with any addresses that had an invalid lat / lng
//                        for (var i = 0; i < routeError.length; i++) {
//                            errorPanel.innerHTML += "<div>" + routeError[i] + "</div>";
//                        }
//                    }

//                    // For each route, display summary information.
//                    for (let i = 0; i < route.legs.length; i++) {
//                        const routeSegment = i + 1;
//                        summaryPanel.innerHTML +=
//                            "<b>Route Segment: " + routeSegment + "</b><br>";
//                        summaryPanel.innerHTML += route.legs[i].start_address + " to ";
//                        summaryPanel.innerHTML += route.legs[i].end_address + "<br>";
//                        summaryPanel.innerHTML += route.legs[i].distance!.text + "<br><br>";
//                    }
//                }




